export const VALS_FUNDING_STATUSES = [
  "Aid in place",
  "Aid not applied for",
  "Aid pending",
  "Extension required",
  "In house",
  "Budget",
  "Other",
];

function inferAppearing(value = "") {
  const text = String(value || "").replace(/^counsel\s+briefed:?\s*/i, "").trim();
  if (!text) return "";
  const match = text.match(/^(.+?)\s+(?:briefed|appearing)(?:\s+for\b.*)?$/i);
  return match ? match[1].trim() : text;
}

function appearingRequiresConfirmation(...values) {
  const text = values.map((value) => String(value || "")).join(" ");
  return /\b(?:advise|confirm)\b/i.test(text)
    && /\b(?:appear(?:ing)?|adj(?:ourn(?:ing|ment)?)?|brief(?:ing|ed)?)\b/i.test(text);
}

export function normalizeValsDiaryEntry(entry = {}) {
  const legacyFundingStatus = {
    "Aid not in place": "Aid not applied for",
    "To be paid from budget": "Other",
  };
  const aidStatus = legacyFundingStatus[entry.aidStatus]
    || entry.aidStatus
    || (entry.legalAidInPlace ? "Aid in place" : "In house");

  const inferredAppearing = /\b(?:briefed|appearing)\b/i.test(String(entry.notes || ""))
    ? inferAppearing(entry.notes)
    : "";

  const appearanceFees = (Array.isArray(entry.appearanceFees)
    ? entry.appearanceFees
    : entry.appearanceFee ? [entry.appearanceFee] : [])
    .map((fee, index) => ({
      id: typeof fee === "object" && fee?.id ? fee.id : `fee_${index}`,
      amount: String(typeof fee === "object" ? fee?.amount || "" : fee || "").trim(),
    }))
    .filter((fee) => fee.amount);

  return {
    ...entry,
    appearing: String(entry.appearing || inferredAppearing).trim(),
    appearingIsBarrister: Boolean(entry.appearingIsBarrister || entry.appearing_is_barrister),
    appearanceFees,
    aidStatus: VALS_FUNDING_STATUSES.includes(aidStatus) ? aidStatus : "Other",
    aidApplicationOnFile: Boolean(entry.aidApplicationOnFile),
    budgetPaymentApprovedByML: Boolean(entry.budgetPaymentApprovedByML),
    aidNumber: entry.aidNumber || "",
    otherStatusText: entry.otherStatusText || "",
  };
}

export function formatValsDiaryDay(value = "") {
  const text = String(value || "").trim();
  if (!text) return "Unscheduled";
  if (/^(Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday)\s+\d{1,2}$/i.test(text)) {
    return text;
  }

  let day;
  let month;
  let year;
  let match = text.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})$/);
  if (match) {
    [, day, month, year] = match;
  } else {
    match = text.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
    if (match) [, year, month, day] = match;
  }

  if (!day || !month || !year) return text;
  const fullYear = Number(year) < 100 ? 2000 + Number(year) : Number(year);
  const date = new Date(Date.UTC(fullYear, Number(month) - 1, Number(day)));
  if (Number.isNaN(date.getTime())) return text;
  const weekday = new Intl.DateTimeFormat("en-AU", { weekday: "long", timeZone: "UTC" }).format(date);
  return `${weekday} ${Number(day)}`;
}

export function valsDiaryDaySortValue(value = "") {
  const order = {
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6,
    sunday: 7,
  };
  return order[String(value).trim().split(/\s+/)[0]?.toLowerCase()] || 99;
}

export function formatValsClientName(value = "") {
  const text = String(value || "").replace(/\s+/g, " ").trim();
  const commaName = text.match(/^([^,]+),\s*(.+)$/);
  return commaName ? `${commaName[2]} ${commaName[1]}`.trim() : text;
}

export function mapParsedDiaryEntries(entries = []) {
  return entries.map((entry, index) => {
    const notes = [entry.outcome, entry.notes]
      .map((part) => String(part || "").trim())
      .filter(Boolean)
      .filter((part, partIndex, parts) => parts.indexOf(part) === partIndex)
      .join(" - ");
    const sectionText = `${entry.diary_section || ""} ${entry.court || ""} ${entry.counsel_briefed || ""} ${notes}`;
    const appearing = appearingRequiresConfirmation(
      entry.appearing,
      entry.counsel_briefed,
      entry.outcome,
      entry.notes,
    )
      ? "TBC"
      : String(entry.appearing || inferAppearing(entry.counsel_briefed)).trim();
    return {
      id: globalThis.crypto?.randomUUID?.() || `vals_${Date.now()}_${index}`,
      date: formatValsDiaryDay(entry.date),
      court: String(entry.court || "Unknown Court").trim() || "Unknown Court",
      clientName: formatValsClientName(entry.client_name),
      appearanceType: String(entry.appearance_type || "").trim(),
      solicitor: String(entry.lawyer_initials || "").trim().toUpperCase(),
      appearing,
      appearingIsBarrister: Boolean(entry.appearing_is_barrister),
      appearanceFees: [],
      notes,
      aidStatus: "In house",
      // Nobody has chosen this status yet, so the Aid Planner may fill it (diaryAidMatch.js).
      aidStatusFromImport: true,
      aidApplicationOnFile: false,
      aidNumber: "",
      solicitorConfirmed: false,
      budgetApprovalSought: false,
      otherStatusText: "",
      isBalitNgulu: /balit\s+ngulu/i.test(sectionText),
    };
  });
}
